import React from "react";
import { Link } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import Banner from "../components/allbanner";
import {
  MessageCircle
} from "lucide-react";

const SalesPage = () => {
  const { trucks, loading, error } = useAppContext();

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          <p className="mt-4 text-gray-600">Loading trucks...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-red-600 font-semibold text-lg">Error: {error}</p>
      </div>
    );
  }

  // Empty state
  if (!trucks || trucks.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">No trucks available.</p>
      </div>
    );
  }

  // Group trucks by category
  const groupedTrucks = trucks.reduce((acc, truck) => {
    if (!acc[truck.category]) acc[truck.category] = [];
    acc[truck.category].push(truck);
    return acc;
  }, {});

  // Custom order for categories
  const orderedCategories = ["N-Series", "F-Series", "C-Series", "Buses"];



  const fSeriesOrder = [
    "FVR90 Rigid",
    "FVR90 Prime",
    "FVR34 Rigid",
    "FVR34 Prime",
    "FVZ34 Rigid",
    "FVZ34 Prime",
    "FXZ60"
  ];



  return (
    <div className="w-full overflow-hidden bg-gray-50">
      {/* Banner Section */}
      <Banner
        title="Powering Pakistan’s Roads"
        description="Explore our range of durable, efficient, and performance-driven Isuzu commercial vehicles."
      />

      {/* WHY ISUZU SECTION */}
      <section className="py-16 px-6 text-center bg-white">
        <h2 className="text-3xl font-bold text-gray-800 mb-10">
          Why Choose <span className="text-red-700">Isuzu Gujranwala Motors</span>?
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            {
              title: "Proven Durability",
              desc: "Built to handle Pakistan’s toughest terrains with unmatched reliability and strength.",
            },
            {
              title: "Exceptional Performance",
              desc: "Powerful engines and advanced engineering that deliver consistent performance on every route.",
            },
            {
              title: "Low Maintenance Costs",
              desc: "Enjoy long service intervals and genuine Isuzu parts that keep operating costs down.",
            },
            {
              title: "Nationwide Service Network",
              desc: "Get reliable after-sales service and spare parts availability across Pakistan.",
            },
          ].map((feature, idx) => (
            <div
              key={idx}
              className="bg-gray-100 hover:bg-red-700 hover:text-white transition-all duration-500 p-8 rounded-2xl shadow-md hover:shadow-xl transform hover:-translate-y-2"
            >
              <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
              <p className="text-sm opacity-80">{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>


      {/* TRUCK LINEUP */}
      <section className="py-16 px-6 bg-gray-50">
        <h2 className="text-3xl font-bold text-gray-800 mb-12 text-center">
          Explore Our Lineup
        </h2>

        {orderedCategories.map((category) => {
          let items = groupedTrucks[category];
          if (!items) return null; // Skip missing categories

          // Reverse N-Series trucks only
          if (category === "F-Series") {
            items = [...items].sort((a, b) => {
              const indexA = fSeriesOrder.findIndex(key =>
                a.name.toLowerCase().includes(key.toLowerCase())
              );
              const indexB = fSeriesOrder.findIndex(key =>
                b.name.toLowerCase().includes(key.toLowerCase())
              );

              return indexA - indexB;
            });
          }

          if (category === "N-Series" || category === "Buses") {
            items = [...items].reverse();
          }


          return (
            <div key={category} className="mb-16">
              <h3 className="text-2xl font-bold text-red-700 mb-6 border-l-4 border-red-700 pl-4">
                {category}
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
                {items.map((truck) => (
                  <Link
                    key={truck.id}
                    to={`/trucks/${truck.slug}`}
                    className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 flex flex-col"
                  >
                    {/* Truck Image */}
                    {truck.image_url && (
                      <div className="relative w-full aspect-[16/9] bg-white flex items-center justify-center overflow-hidden">
                        <img
                          src={truck.image_url}
                          alt={truck.name}
                          className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                    )}


                    {/* Truck Info */}
                    <div className="flex flex-col flex-grow p-6">
                      {/* Name */}
                      <h4 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-red-700 transition-colors">
                        {truck.name}
                      </h4>

                      {/* Description */}
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2 flex-grow">
                        {truck.small_description}
                      </p>

                      {/* Price */}


                      {/* Bottom Section */}
                      <div className="mt-auto border-t border-gray-200 pt-4">
                        {/* Key Specs */}
                        {truck.specifications && (
                          <ul className="text-xs text-gray-600 space-y-1 mb-4">
                            {truck.specifications.model && (
                              <li>
                                <span className="font-semibold">Model:</span>{" "}
                                {truck.specifications.model}
                              </li>
                            )}
                            {truck.specifications.engine && (
                              <li>
                                <span className="font-semibold">Engine:</span>{" "}
                                {truck.specifications.engine}
                              </li>
                            )}
                            {truck.specifications.wheelbase && (
                              <li>
                                <span className="font-semibold">
                                  Wheelbase:
                                </span>{" "}
                                {truck.specifications.wheelbase}
                              </li>
                            )}
                          </ul>
                        )}

                        {/* View Details (visual button only) */}
                        <div className="w-full text-center bg-red-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-300 group-hover:bg-red-700">
                          View Details
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </section>

      {/* CTA Section */}
      <section className="w-full py-20 px-6 text-white bg-gradient-to-r from-[#D71920] to-[#A50E0E] text-center">
        <h2 className="text-4xl font-extrabold mb-4 animate-fadeInUp">
          Ready to Drive with Isuzu?
        </h2>

        <p className="max-w-2xl mx-auto mb-10 text-lg opacity-90 animate-fadeInUp delay-100">
          Connect with our professional sales team today and let us help you choose
          the perfect Isuzu vehicle for your business.
        </p>

        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 animate-fadeInUp delay-200">

          {/* Contact Button */}
          <a
            href="/contact"
            className="
        bg-white text-[#D71920] font-bold px-8 py-3 
        rounded-xl shadow-lg hover:bg-gray-100 
        transition-all inline-flex items-center gap-2
      "
          >
            Contact Sales
          </a>

          {/* WhatsApp Button */}
          <a
            href={`https://wa.me/923129388531?text=${encodeURIComponent(
              "Hey I want to inquire about Isuzu vehicles"
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="
        bg-green-500 hover:bg-green-600 text-white 
        font-semibold px-8 py-3 rounded-xl 
        shadow-lg transition-all inline-flex items-center gap-2
      "
          >
            <MessageCircle className="w-5 h-5" />
            WhatsApp Sales
          </a>
        </div>
      </section>

    </div>
  );
};

export default SalesPage;
